import { StateSource } from "./StateSource.js";
import { RolledGameState } from "../engine/state/RolledGameState.js";
import type { Engine } from "../engine/Engine.js";
import { PlayerType } from "../model/PlayerType.js";
import type { Roll } from "../model/dice/Roll.js";
import { WaitingForRollGameState } from "../engine/state/WaitingForRollGameState.js";
import { WaitingForMoveGameState } from "../engine/state/WaitingForMoveGameState.js";
import { MovedGameState } from "../engine/state/MovedGameState.js";
import { EndGameState } from "../engine/state/EndGameState.js";
import type { Move } from "../model/Move.js";
import type { Board } from "../model/Board.js";
import type { PlayerState } from "../model/PlayerState.js";
import { ResignedGameState } from "../engine/state/ResignedGameState.js";
import type { AbandonReason } from "../model/AbandonReason.js";
import { AbandonedGameState } from "../engine/state/AbandonedGameState.js";

/**
 * Produces game states from scratch.
 */
export class FullStateSource extends StateSource {
    private readonly board: Board;
    private readonly lightPlayer: PlayerState;
    private readonly darkPlayer: PlayerState;

    constructor(
        board: Board,
        lightPlayer: PlayerState,
        darkPlayer: PlayerState,
    ) {
        super();
        this.board = board;
        this.lightPlayer = lightPlayer;
        this.darkPlayer = darkPlayer;
    }

    private getPlayer(player: PlayerType): PlayerState {
        if (player === PlayerType.LIGHT)
            return this.lightPlayer;
        if (player === PlayerType.DARK)
            return this.darkPlayer;
        throw new Error(`Unknown player ${player.getName()}`);
    }

    override createRolledState(
        rules: Engine,
        timeSinceGameStartMs: number,
        turn: PlayerType,
        roll: Roll,
    ): RolledGameState {
        const availableMoves = rules.findAvailableMoves(
            this.board, this.getPlayer(turn), roll,
        );
        return new RolledGameState(
            this.board, this.lightPlayer, this.darkPlayer,
            timeSinceGameStartMs, turn, roll, availableMoves,
        );
    }

    override createMovedState(
        _rules: Engine,
        timeSinceGameStartMs: number,
        turn: PlayerType,
        roll: Roll,
        move: Move,
    ): MovedGameState {
        return new MovedGameState(
            this.board, this.lightPlayer, this.darkPlayer,
            timeSinceGameStartMs, turn, roll, move,
        );
    }

    override createWaitingForRollState(
        _rules: Engine,
        timeSinceGameStartMs: number,
        turn: PlayerType,
    ): WaitingForRollGameState {
        return new WaitingForRollGameState(
            this.board, this.lightPlayer, this.darkPlayer, timeSinceGameStartMs, turn,
        );
    }

    override createWaitingForMoveState(
        rules: Engine,
        timeSinceGameStartMs: number,
        turn: PlayerType,
        roll: Roll,
    ): WaitingForMoveGameState {
        const availableMoves = rules.findAvailableMoves(
            this.board, this.getPlayer(turn), roll,
        );
        return new WaitingForMoveGameState(
            this.board, this.lightPlayer, this.darkPlayer,
            timeSinceGameStartMs, turn, roll, availableMoves,
        );
    }

    override createResignedState(
        _rules: Engine,
        timeSinceGameStartMs: number,
        player: PlayerType,
    ): ResignedGameState {
        return new ResignedGameState(
            this.board, this.lightPlayer, this.darkPlayer, timeSinceGameStartMs, player,
        );
    }

    override createAbandonedState(
        _rules: Engine,
        timeSinceGameStartMs: number,
        reason: AbandonReason,
        player: PlayerType | null,
    ): AbandonedGameState {
        return new AbandonedGameState(
            this.board, this.lightPlayer, this.darkPlayer, timeSinceGameStartMs, reason, player,
        );
    }

    override createEndState(
        _rules: Engine,
        timeSinceGameStartMs: number,
        winner: PlayerType | null,
    ): EndGameState {
        return new EndGameState(
            this.board, this.lightPlayer, this.darkPlayer, timeSinceGameStartMs, winner,
        );
    }
}
